import {
  decodeMaterialsTab,
  encodeMaterialRef,
  findMaterial,
  firstMaterialOfType,
  landingSourceItem,
  type LegacyDocTab,
  MATERIALS_OVERVIEW_TAB,
  sameMaterialRef,
} from './ref-codec';
import type { DeckMaterialItem, DeckMaterialRef } from './types';

/**
 * The source pane's TABS — the browser model, not a segmented control.
 *
 * The strip used to list every material of the deck with one of them active.
 * It now lists only what the user has OPENED, each closable, so the strip is
 * a workspace rather than an index: the index is the overview grid, which is
 * always reachable and is where the pane lands with nothing open.
 *
 * `view` is kept alongside `tabs` so every consumer that only asks "is the
 * overview showing, and if not which material" reads exactly as before. The
 * two are not independent: `view: 'item'` always names a ref that is in
 * `tabs`, which is what the constructors below exist to guarantee.
 *
 * `tabs` is kept in RECENCY order — the tab focused last is the tail. Nothing
 * draws that order (the strip is drawn in the deck's, see
 * {@link orderMaterialTabs}); it is what "the one you were last in" is read
 * from. Below 640px on web, and on the phone, nothing ever closes a tab, so
 * an open order would answer "the one opened last": A, then B, then back to
 * A, then Notes — and the Document segment returned to B.
 */
export type MaterialsPaneState = { tabs: readonly DeckMaterialRef[] } & (
  | { view: 'overview' }
  | { view: 'item'; ref: DeckMaterialRef }
);

export const MATERIALS_OVERVIEW: MaterialsPaneState = { view: 'overview', tabs: [] };

/** `tabs` with `ref` moved to (or added at) the tail — the most recent. */
function withRecent(
  tabs: readonly DeckMaterialRef[],
  ref: DeckMaterialRef,
): readonly DeckMaterialRef[] {
  const at = tabs.findIndex((tab) => sameMaterialRef(tab, ref));
  if (at >= 0 && at === tabs.length - 1) return tabs;
  return [...tabs.filter((_, index) => index !== at), ref];
}

/**
 * Open a material: focus its tab, adding one if it is not already open.
 * Opening something already open only moves focus — a browser does not give
 * you two tabs for the same page from the same click. Either way it becomes
 * the most recent tab (see {@link MaterialsPaneState}).
 */
export function openMaterial(
  state: MaterialsPaneState,
  ref: DeckMaterialRef,
): MaterialsPaneState {
  return { view: 'item', ref, tabs: withRecent(state.tabs, ref) };
}

/**
 * The tabs AS THE STRIP DRAWS THEM: in the deck's materials order (`items`),
 * not the order they were opened in.
 *
 * `tabs` itself stays in recency order — it is the per-device workspace, and
 * "the note you were last in" is read off its tail — but the strip is a view
 * of the deck's list, and a reorder (dragging a tab or a tile) is a deck-wide
 * fact that both surfaces must show the same way, or a tab dragged to the
 * front on one device would sit anywhere on the other. A tab whose material
 * the items do not (yet) hold keeps its place at the end.
 */
export function orderMaterialTabs(
  tabs: readonly DeckMaterialRef[],
  items: readonly DeckMaterialItem[],
): DeckMaterialRef[] {
  const at = new Map(items.map((item, index) => [item.key, index]));
  const rank = (ref: DeckMaterialRef) => at.get(encodeMaterialRef(ref)) ?? items.length;
  return [...tabs].sort((a, b) => rank(a) - rank(b));
}

/**
 * Close a tab.
 *
 * Focus follows the browser: the NEIGHBOUR to the right takes over, or the
 * one to the left when the closed tab was last. Closing the final tab lands
 * on the overview — there is nothing else to show, and it is the view that
 * lets the user open something again. Closing a tab that is not the active
 * one leaves focus exactly where it is.
 *
 * "Right" and "left" are the strip's — pass `items` and the neighbour is the
 * one DRAWN beside the closed tab (see {@link orderMaterialTabs}); without
 * them it is the neighbour in `tabs` order. The neighbour that takes focus
 * becomes the most recent tab.
 */
export function closeMaterial(
  state: MaterialsPaneState,
  ref: DeckMaterialRef,
  items?: readonly DeckMaterialItem[],
): MaterialsPaneState {
  const index = state.tabs.findIndex((tab) => sameMaterialRef(tab, ref));
  if (index < 0) return state;
  const tabs = state.tabs.filter((_, at) => at !== index);

  const wasActive = state.view === 'item' && sameMaterialRef(state.ref, ref);
  if (!wasActive) {
    return state.view === 'overview'
      ? { view: 'overview', tabs }
      : { view: 'item', ref: state.ref, tabs };
  }
  const drawn = items ? orderMaterialTabs(state.tabs, items) : state.tabs;
  const drawnIndex = drawn.findIndex((tab) => sameMaterialRef(tab, ref));
  const remaining = drawn.filter((_, at) => at !== drawnIndex);
  const next = remaining[drawnIndex] ?? remaining[drawnIndex - 1];
  return next
    ? { view: 'item', ref: next, tabs: withRecent(tabs, next) }
    : { view: 'overview', tabs };
}

/** Show the overview WITHOUT closing anything — the tabs stay open. */
export function showMaterialsOverview(state: MaterialsPaneState): MaterialsPaneState {
  return { view: 'overview', tabs: state.tabs };
}

/** The tab the strip must keep on screen, or -1 on the overview — its index
 *  AS DRAWN when `items` are given (see {@link orderMaterialTabs}). */
export function activeTabIndex(
  state: MaterialsPaneState,
  items?: readonly DeckMaterialItem[],
): number {
  if (state.view === 'overview') return -1;
  const tabs = items ? orderMaterialTabs(state.tabs, items) : state.tabs;
  return tabs.findIndex((tab) => sameMaterialRef(tab, state.ref));
}

export function encodeMaterialsPaneState(state: MaterialsPaneState): string {
  return state.view === 'overview'
    ? MATERIALS_OVERVIEW_TAB
    : encodeMaterialRef(state.ref);
}

/** The open set, as the per-deck persisted view stores it. */
export function encodeMaterialsTabs(state: MaterialsPaneState): string[] {
  return state.tabs.map(encodeMaterialRef);
}

/**
 * Where the pane lands when nothing is persisted: the deck's PRIMARY source
 * open as its one tab — wherever a reorder has drawn it — else, on a deck with
 * no readable source, its first note, else whatever is drawn first. That is the
 * pre-multi-source landing, so no deck opens differently because someone
 * dragged a note or a second document to the front of the strip.
 */
export function defaultMaterialsPaneState(
  items: readonly DeckMaterialItem[],
  primarySourceId: string | undefined,
): MaterialsPaneState {
  const landing =
    landingSourceItem(items, primarySourceId) ??
    firstMaterialOfType(items, 'note') ??
    items[0];
  return landing ? openMaterial(MATERIALS_OVERVIEW, landing.ref) : MATERIALS_OVERVIEW;
}

/**
 * Re-validate a state against the current items. A tab whose material is gone
 * — removed here or on another device — closes itself, so a deck that loses
 * the document you were reading does not dump you on the overview while other
 * tabs are still open.
 *
 * Focus goes back to the tab you were in BEFORE the removed one (its
 * predecessor in `tabs`, which is recency order), not to the neighbour a
 * deliberate × picks: a material that is no longer in `items` has no drawn
 * place, so "the tab drawn beside it" does not exist any more.
 */
export function reconcileMaterialsPaneState(
  state: MaterialsPaneState,
  items: readonly DeckMaterialItem[],
): MaterialsPaneState {
  let next = state;
  for (const tab of state.tabs) {
    if (!findMaterial(items, tab)) next = closeMaterial(next, tab);
  }
  // The active ref can also be stale without being in `tabs` at all (a
  // persisted `?tab=` for a material since removed).
  if (next.view === 'item' && !findMaterial(items, next.ref)) {
    return { view: 'overview', tabs: next.tabs };
  }
  return next;
}

/**
 * Resolve the persisted / URL values into a state.
 *
 * `active` is the `?tab=` value — a shared link carries which material to
 * show, and nothing else. `openTabs` is the device's own workspace, so
 * someone opening your link gets that one material rather than your tab set.
 * An active material missing from the stored set still opens as a tab: a
 * link is an open.
 *
 * `primarySourceId` is REQUIRED (pass `undefined` when the deck has none):
 * the legacy `document` value and the landing both mean the primary, and the
 * items alone cannot say which source that is once the strip is reordered.
 */
export function materialsPaneStateFromTab(
  active: string | null | undefined,
  items: readonly DeckMaterialItem[],
  openTabs: readonly string[] | null | undefined,
  primarySourceId: string | undefined,
): MaterialsPaneState {
  const restored: DeckMaterialRef[] = [];
  for (const raw of openTabs ?? []) {
    const decoded = decodeMaterialsTab(raw, items, primarySourceId);
    if (
      decoded?.view === 'item' &&
      !restored.some((r) => sameMaterialRef(r, decoded.ref))
    )
      restored.push(decoded.ref);
  }
  const base: MaterialsPaneState = { view: 'overview', tabs: restored };

  const decoded = decodeMaterialsTab(active, items, primarySourceId);
  if (!decoded) {
    // No usable active value (none stored, or a renderer view like `split`):
    // keep the restored tabs and show the one the user was last in — the
    // stored set is in recency order — else the deck's default landing.
    // Entering Split while reading a second document must not swap it out.
    const recent = restored[restored.length - 1];
    return recent
      ? openMaterial(base, recent)
      : defaultMaterialsPaneState(items, primarySourceId);
  }
  return decoded.view === 'overview' ? base : openMaterial(base, decoded.ref);
}

export function isMaterialOpen(state: MaterialsPaneState, ref: DeckMaterialRef): boolean {
  return state.view === 'item' && sameMaterialRef(state.ref, ref);
}

/**
 * WHICH note (or map) a surface dedicated to that kind is showing.
 *
 * The phone hosts its notes and its maps on their own tabs, so arriving at one
 * is not opening a material: the pane's own ref goes on naming whatever the
 * Documents surface holds, which on every deck with a readable document is a
 * DOCUMENT. Asking the ref alone therefore answered "a source, so — the
 * deck's first note", and a deck with five notes opened note 1 every time you
 * came back to the tab. Switching between them was impossible.
 *
 * Three answers, in order:
 *   1. the pane IS on one of that kind — that one;
 *   2. otherwise the one the user was last IN (not merely opened last), which is still in the strip
 *      (open tabs are remembered per deck, so this survives leaving the deck);
 *   3. otherwise the deck's first, which is where a deck that has never
 *      opened one starts.
 *
 * One rule, so the surface and the switcher chip above it cannot name two
 * different notes.
 */
export function activeMaterialOfType(
  state: MaterialsPaneState,
  items: readonly DeckMaterialItem[],
  type: DeckMaterialRef['type'],
): DeckMaterialRef | undefined {
  return lastActiveOfType(state, items, type) ?? firstMaterialOfType(items, type)?.ref;
}

/**
 * The material of `type` the user was last IN: the focused one when it is of
 * that kind, else the most recent tab of that kind the deck still lists (and
 * `accept` takes). `undefined` when none of that kind has been opened. The one
 * reading behind {@link activeMaterialOfType}, {@link refForLegacyTab} and the
 * active document (`resolveActiveDocument`).
 */
export function lastActiveOfType(
  state: MaterialsPaneState,
  items: readonly DeckMaterialItem[],
  type: DeckMaterialRef['type'],
  accept: (item: DeckMaterialItem) => boolean = () => true,
): DeckMaterialRef | undefined {
  const recent = state.view === 'item' ? [...state.tabs, state.ref] : state.tabs;
  for (let at = recent.length - 1; at >= 0; at -= 1) {
    const tab = recent[at];
    if (tab.type !== type) continue;
    const item = findMaterial(items, tab);
    if (item && accept(item)) return tab;
  }
  return undefined;
}

/**
 * What a THREE-WAY Document / Notes / Mindmaps control opens when one of its
 * segments is pressed (web below 640px, where there is no tab strip).
 *
 * Switching kind returns to the one of that kind you were last in — the
 * document you were reading, not the deck's first — so flipping to Notes and
 * back never loses your place. With nothing of the kind open yet, Document
 * lands on the primary and Notes / Mindmaps on the first as drawn: the same
 * targets as the legacy `?tab=` aliases. `undefined` when the deck has
 * nothing of that kind.
 */
export function refForLegacyTab(
  tab: LegacyDocTab,
  state: MaterialsPaneState,
  items: readonly DeckMaterialItem[],
  primarySourceId: string | undefined,
): DeckMaterialRef | undefined {
  if (tab === 'notes') return activeMaterialOfType(state, items, 'note');
  if (tab === 'mindmaps') return activeMaterialOfType(state, items, 'mindmap');
  if (state.view === 'item' && state.ref.type === 'source') return state.ref;
  return (
    lastActiveOfType(state, items, 'source') ??
    landingSourceItem(items, primarySourceId)?.ref
  );
}

/** Is this material one of the open tabs (focused or not)? */
export function hasMaterialTab(state: MaterialsPaneState, ref: DeckMaterialRef): boolean {
  return state.tabs.some((tab) => sameMaterialRef(tab, ref));
}

/**
 * The materials that are NOT open as a tab — what the "+" menu's "Recents"
 * lists. A material that is already in the strip has nothing to offer there:
 * "open" would only move focus to a tab that is one click away anyway, and a
 * list that repeats the strip reads as if the menu did not know what was
 * open. Order is the deck's own (`items` order), not the tab order.
 */
export function unopenedMaterials(
  items: readonly DeckMaterialItem[],
  state: MaterialsPaneState,
): DeckMaterialItem[] {
  return items.filter((item) => !hasMaterialTab(state, item.ref));
}

/**
 * Does the mobile Documents surface show the OVERVIEW rather than a document?
 *
 * That surface has exactly two states — one document open, or the grid of
 * every material — and which one it shows is not only the pane's state: a deck
 * with no readable source has no document to fall back to, so the grid is the
 * only thing it can honestly be. This is what lets a deck built from nothing
 * still reach its note, its map and the way to add a source; before it, the
 * whole surface was hidden on such a deck and there was no second note to be
 * had. Web needs no equivalent: its pane is one body that switches on the open
 * material.
 */
export function documentsSurfaceShowsOverview(input: {
  state: MaterialsPaneState;
  hasSource: boolean;
}): boolean {
  return !input.hasSource || input.state.view === 'overview';
}
